/**
 * Step — Filters. Multi-select with real thumbnails (cachedThumbnail) when
 * available, tinted swatch fallback otherwise, and a title search.
 *
 * "Try it with your photo": instead of a separate before/after strip, EVERY
 * filter card re-renders with the user's test photo through that filter —
 * progressively and without blocking the UI — using the SAME pipeline the
 * booth uses (filterUtils adjustments / LUT + overlay), so what the creator
 * sees is what guests get. The photo persists in localStorage so a returning
 * creator doesn't have to re-shoot it.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FaSpinner, FaSearch, FaCamera, FaUpload, FaTimes } from 'react-icons/fa'
import { useOnboardingStore } from '../onboardingStore'
import SelectableCard from '../components/SelectableCard'
import { filterUtils } from '@renderer/utils/filterUtils'
import {
  applyLUTThenOverlay,
  fetchLUTData,
  fetchOverlayImage,
  applyFilterOverlayImage
} from '@renderer/utils/lutUtils'
import CameraCaptureModal from '../components/CameraCaptureModal'
import type { CatalogFilter } from '../onboardingApi'
import { useCopy } from '../regionCopy'

const TRY_PHOTO_KEY = 'db.onboardingTryPhoto'
/** Card previews render small; the stored master stays ≤720px. */
const STORE_MAX_PX = 720
const CARD_MAX_PX = 360
const RENDER_CONCURRENCY = 4

function loadStoredTryPhoto(): string | null {
  try {
    const v = localStorage.getItem(TRY_PHOTO_KEY)
    return v && v.startsWith('data:image') ? v : null
  } catch {
    return null
  }
}

/** Downscale to ≤maxPx JPEG so localStorage stays well under quota. */
async function normalizePhoto(dataUrl: string, maxPx: number): Promise<string> {
  const res = await fetch(dataUrl)
  const bitmap = await createImageBitmap(await res.blob())
  const scale = Math.min(1, maxPx / Math.max(bitmap.width, bitmap.height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(bitmap.width * scale)
  canvas.height = Math.round(bitmap.height * scale)
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('no 2d context')
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  bitmap.close()
  return canvas.toDataURL('image/jpeg', 0.82)
}

/** Renders the test photo through one filter, returns a small JPEG data URL. */
async function renderFiltered(base: ImageBitmap, filter: CatalogFilter): Promise<string> {
  const scale = Math.min(1, CARD_MAX_PX / Math.max(base.width, base.height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(base.width * scale)
  canvas.height = Math.round(base.height * scale)
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('no 2d context')
  ctx.drawImage(base, 0, 0, canvas.width, canvas.height)
  // One-off thumbnail render outside the session flow, so nothing has warmed the caches
  // for it — await both here rather than drawing a filter with half its look.
  if (filter.overlay?.url) {
    await fetchOverlayImage(filter.overlay.url)
  }
  if (filter.filterMode === 'lut') {
    await fetchLUTData(filter._id)
    applyLUTThenOverlay(ctx, {
      _id: filter._id,
      filterMode: 'lut',
      postLUTAdjustments: filter.postLUTAdjustments,
      overlay: filter.overlay
    })
  } else if (filter.adjustments) {
    filterUtils.applyAdjustments(ctx, filter.adjustments)
    applyFilterOverlayImage(ctx, filter.overlay)
  }
  return canvas.toDataURL('image/jpeg', 0.8)
}

/**
 * Progressive per-filter previews of the test photo. Non-blocking: cards fill
 * in as renders finish (bounded concurrency), keyed by filter id.
 */
function useFilteredPreviews(
  testPhoto: string | null,
  filters: CatalogFilter[]
): Record<string, string> {
  const [previews, setPreviews] = useState<Record<string, string>>({})

  useEffect(() => {
    setPreviews({})
    if (!testPhoto || filters.length === 0) return
    let cancelled = false
    void (async (): Promise<void> => {
      let base: ImageBitmap | null = null
      try {
        const res = await fetch(testPhoto)
        base = await createImageBitmap(await res.blob())
        const queue = [...filters]
        const worker = async (): Promise<void> => {
          while (!cancelled) {
            const f = queue.shift()
            if (!f) return
            try {
              const url = await renderFiltered(base as ImageBitmap, f)
              if (!cancelled) setPreviews((prev) => ({ ...prev, [f._id]: url }))
            } catch {
              // One bad filter must not stop the rest — the card keeps its
              // regular thumbnail.
            }
          }
        }
        await Promise.all(
          Array.from({ length: Math.min(RENDER_CONCURRENCY, queue.length) }, () => worker())
        )
      } catch {
        // Photo unreadable — cards keep their regular thumbnails.
      } finally {
        base?.close()
      }
    })()
    return () => {
      cancelled = true
    }
  }, [testPhoto, filters])

  return previews
}

interface StepFiltersV2Props {
  official: CatalogFilter[]
  community: CatalogFilter[]
  loading: boolean
}

/** Tinted swatch fallback when a filter has no cached thumbnail. */
function swatchStyle(f: CatalogFilter): React.CSSProperties {
  const a = f.adjustments || {}
  const temp = (a.temperature ?? 0) as number
  const sat = (a.saturation ?? 100) as number
  const hue = temp >= 0 ? 35 : 210
  const sl = Math.max(10, Math.min(60, sat / 2))
  return {
    background: `linear-gradient(135deg, hsl(${hue} ${sl}% 55%) 0%, hsl(${hue} ${sl}% 35%) 100%)`
  }
}

const FilterGrid: React.FC<{
  items: CatalogFilter[]
  previews: Record<string, string>
  hasTestPhoto: boolean
}> = ({ items, previews, hasTestPhoto }) => {
  const filterIds = useOnboardingStore((s) => s.filterIds)
  const toggleFilter = useOnboardingStore((s) => s.toggleFilter)
  return (
    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-5">
      {items.map((f) => {
        const src = previews[f._id] ?? f.cachedThumbnail
        const waitingForPhoto = hasTestPhoto && !previews[f._id]
        return (
          <SelectableCard
            key={f._id}
            selected={filterIds.includes(f._id)}
            onClick={() => toggleFilter(f._id)}
            ariaLabel={f.name}
            className="bg-white/[0.04] p-0"
          >
            <div className="relative flex aspect-square w-full items-center justify-center overflow-hidden">
              {src ? (
                <img src={src} alt={f.name} loading="lazy" className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full" style={swatchStyle(f)} />
              )}
              {waitingForPhoto && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <FaSpinner className="animate-spin text-sm text-whiteout/80" />
                </span>
              )}
            </div>
            <div className="truncate px-2 py-1.5 text-[11px] text-whiteout/70">{f.name}</div>
          </SelectableCard>
        )
      })}
    </div>
  )
}

export const StepFiltersV2: React.FC<StepFiltersV2Props> = ({ official, community, loading }) => {
  const filterIds = useOnboardingStore((s) => s.filterIds)
  const [query, setQuery] = useState('')
  const [testPhoto, setTestPhoto] = useState<string | null>(() => loadStoredTryPhoto())
  const [cameraOpen, setCameraOpen] = useState(false)
  const c = useCopy()
  const cameraRef = useRef<HTMLInputElement>(null)
  const uploadRef = useRef<HTMLInputElement>(null)

  const applyTestPhoto = (dataUrl: string): void => {
    void normalizePhoto(dataUrl, STORE_MAX_PX)
      .then((normalized) => {
        setTestPhoto(normalized)
        try {
          localStorage.setItem(TRY_PHOTO_KEY, normalized)
        } catch {
          // Quota/disabled storage — the in-memory photo still works this session.
        }
      })
      .catch(() => setTestPhoto(dataUrl))
  }

  const clearTestPhoto = (): void => {
    setTestPhoto(null)
    try {
      localStorage.removeItem(TRY_PHOTO_KEY)
    } catch {
      // ignore
    }
  }

  const handleTestFile = (file: File | undefined): void => {
    if (!file || !file.type.startsWith('image/')) return
    const reader = new FileReader()
    reader.onload = () => applyTestPhoto(String(reader.result))
    reader.readAsDataURL(file)
  }

  const q = query.trim().toLowerCase()
  const match = (f: CatalogFilter): boolean => !q || f.name.toLowerCase().includes(q)
  const officialFiltered = useMemo(() => official.filter(match), [official, q])
  const communityFiltered = useMemo(() => community.filter(match), [community, q])

  // One render pass covers every visible filter (official + community).
  const allVisible = useMemo(
    () => [...officialFiltered, ...communityFiltered],
    [officialFiltered, communityFiltered]
  )
  const previews = useFilteredPreviews(testPhoto, allVisible)

  return (
    <div className="mx-auto flex h-full w-full max-w-3xl flex-col gap-4 px-4 py-6 sm:px-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">{c.filters}</h2>
          <p className="text-sm text-whiteout/50">
            {c.filtersSub}{' '}
            {filterIds.length > 0 && (
              <span className="text-blueSparkle">{c.nSelected(filterIds.length)}</span>
            )}
          </p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="user"
            className="hidden"
            onChange={(e) => handleTestFile(e.target.files?.[0])}
          />
          <input
            ref={uploadRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleTestFile(e.target.files?.[0])}
          />
          {testPhoto ? (
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 py-1 pl-1 pr-3 text-xs text-whiteout/80">
              <img
                src={testPhoto}
                alt={c.usingYourPhoto}
                className="h-6 w-6 rounded-full object-cover ring-1 ring-white/20"
              />
              {c.usingYourPhoto}
              <button
                type="button"
                onClick={clearTestPhoto}
                aria-label={c.removeTestPhoto}
                className="text-whiteout/50 hover:text-whiteout"
              >
                <FaTimes />
              </button>
            </span>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setCameraOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-2 text-xs text-whiteout/70 hover:border-blueSparkle/60 hover:text-whiteout"
                title={c.tryItHint}
              >
                <FaCamera /> {c.tryIt}
              </button>
              <button
                type="button"
                onClick={() => uploadRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-2 text-xs text-whiteout/70 hover:border-blueSparkle/60 hover:text-whiteout"
                title={c.uploadTestPhoto}
              >
                <FaUpload />
              </button>
            </>
          )}
          <label className="relative min-w-0 flex-1 sm:flex-none">
            <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-whiteout/30" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={c.searchFilters}
              className="w-full rounded-full border border-white/15 bg-white/[0.04] py-2 pl-8 pr-3 text-xs text-whiteout placeholder-whiteout/30 outline-none focus:border-blueSparkle sm:w-44"
            />
          </label>
        </div>
      </header>

      {cameraOpen && (
        <CameraCaptureModal
          onCapture={(dataUrl) => applyTestPhoto(dataUrl)}
          onClose={() => setCameraOpen(false)}
          onUnavailable={() => {
            // No webcam / permission denied → native capture input (mobile
            // opens the camera; desktop falls back to the file picker).
            setCameraOpen(false)
            cameraRef.current?.click()
          }}
        />
      )}

      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto soft-scrollbar pb-4 pr-1">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-whiteout/50">
            <FaSpinner className="animate-spin text-2xl" />
          </div>
        ) : (
          <>
            {officialFiltered.length > 0 && (
              <section className="space-y-3">
                <h3 className="text-xs font-medium uppercase tracking-wide text-whiteout/40">
                  {c.dreamboothPicks}
                </h3>
                <FilterGrid
                  items={officialFiltered}
                  previews={previews}
                  hasTestPhoto={Boolean(testPhoto)}
                />
              </section>
            )}
            {communityFiltered.length > 0 && (
              <section className="space-y-3">
                <h3 className="text-xs font-medium uppercase tracking-wide text-whiteout/40">
                  {c.community}
                </h3>
                <FilterGrid
                  items={communityFiltered}
                  previews={previews}
                  hasTestPhoto={Boolean(testPhoto)}
                />
              </section>
            )}
            {officialFiltered.length === 0 && communityFiltered.length === 0 && (
              <p className="py-12 text-center text-sm text-whiteout/40">
                {c.noFiltersMatch(query)}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default StepFiltersV2
